import './FacetGroupContent.scss';
import React, {useCallback, useMemo} from 'react';
import {Histogram, HistogramSnapPoint} from '../../../../histogram/histogram';
import {FacetValue, HibuFilter, HibuRangeFilter} from '../../../facet.types';
import {changePendingState, selectFilter, unselectFilter} from '../../../duck/filter.actions';
import {filterFacets} from '../../../facet.service';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {formatDateShort} from '../../../../utils/utils';
import {Checkbox} from '../../../../common/checkbox/checkbox';

type FacetGroupHistogramProps = {
    isHistogram: boolean;
    isExpanded: boolean;
    values: FacetValue[];
    fieldName: string;
    selectedFilters: HibuFilter[];
};

export const FacetGroupHistogram = React.memo(({
                                                   isHistogram,
                                                   values,
                                                   fieldName,
                                                   isExpanded,
                                                   selectedFilters,
                                               }: FacetGroupHistogramProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {snapPoints, range} = useMemo(() => {
        const points: number[] = [];
        const sps: HistogramSnapPoint[] = [];

        values.forEach((facet: FacetValue) => {
            const rangeFilter = facet.filter as HibuRangeFilter;
            const x0: number = new Date(rangeFilter.gte).valueOf();
            const x: number = new Date(rangeFilter.lt).valueOf();
            points.push(x0, x);
            sps.push(facet);
        });

        const sortedData: number[] = points
            .filter((item, pos, self) => self.indexOf(item) === pos) // filter duplicates
            .sort((a, b) => a - b);

        return {
            snapPoints: sps,
            range: [sortedData[0], sortedData.at(-1) || sortedData.length],
        };
    }, [values]);

    const initialState: number[] | undefined = useMemo(() => {
        if (selectedFilters?.length > 0) {
            return [new Date((selectedFilters as HibuRangeFilter[])[0].gte).valueOf(), new Date((selectedFilters as HibuRangeFilter[])[0].lt).valueOf()];
        } else {
            return [new Date(range[0]).valueOf(), new Date(range[1]).valueOf()];
        }
    }, [range, selectedFilters]);

    const onFacetRangeRemoved = (): void => {
        if (selectedFilters?.length > 0) {
            dispatch(changePendingState(true));
            dispatch(unselectFilter(selectedFilters[0], fieldName));
            dispatch(filterFacets(navigate)); // trigger search (fetchFacets and searchDocuments)
        }
    };

    const onFacetRangeUpdated = useCallback((lowerBound: number, upperBound: number): void => {
        dispatch(changePendingState(true));
        const lowerBoundISOString = new Date(lowerBound).toISOString();
        const upperBoundISOString = new Date(upperBound).toISOString();
        if (selectedFilters?.length > 0) { // if there is a RangeFilter already present, we remove this first
            dispatch(unselectFilter(selectedFilters[0], fieldName));
        }
        dispatch(selectFilter(new HibuRangeFilter(lowerBoundISOString, upperBoundISOString), fieldName));
        dispatch(filterFacets(navigate)); // trigger search (fetchFacets and searchDocuments)
    }, [dispatch, fieldName, navigate, selectedFilters]);

    if (!isHistogram) return null;
    return (
        <>
            <HistogramExpanded
                isExpanded={isExpanded}
                snapPoints={snapPoints}
                range={range}
                initialState={initialState}
                onFacetRangeUpdated={([lb, ub]: number[]) => onFacetRangeUpdated(lb, ub)}/>
            <HistogramCollapsed
                isExpanded={isExpanded} selectedFilters={selectedFilters}
                removeSelection={() => onFacetRangeRemoved()}/>
        </>
    );
}, histogramPropsAreEqual);

/**
 * To increase rendering performance: Make a manual check if the histogram component should rerender or not
 */
function histogramPropsAreEqual(prev: FacetGroupHistogramProps, next: FacetGroupHistogramProps): boolean {
    if (
        prev.values.length !== next.values.length ||
        prev.selectedFilters?.length !== next.selectedFilters?.length ||
        prev.fieldName !== next.fieldName ||
        prev.isExpanded !== next.isExpanded ||
        prev.isHistogram !== next.isHistogram
    ) {
        return false;
    }

    // Compare selectedFilters
    if (prev.selectedFilters && next.selectedFilters) {
        for (let i = 0; i < prev.selectedFilters.length; i++) {
            if (prev.selectedFilters[i] !== next.selectedFilters[i]) {
                return false;
            }
        }
    }

    // Compare values
    for (let i = 0; i < prev.values.length; i++) {
        if (prev.values[i] !== next.values[i]) {
            return false;
        }
    }

    return true;
}

type HistogramExpandedProps = {
    isExpanded: boolean;
    snapPoints: HistogramSnapPoint[];
    range: number[];
    initialState: number[] | undefined;
    onFacetRangeUpdated: ([lb, ub]: number[]) => void;
};

const HistogramExpanded = React.memo(({
                                          isExpanded,
                                          snapPoints,
                                          range,
                                          initialState,
                                          onFacetRangeUpdated
                                      }: HistogramExpandedProps) => {
    if (!isExpanded) return null;
    return (
        <Histogram
            snapPoints={snapPoints}
            range={range}
            selection={initialState}
            handleSelection={([lb, ub]: number[]) => onFacetRangeUpdated([lb, ub])}
        />
    );
});

type HistogramCollapsedProps = {
    isExpanded: boolean;
    selectedFilters: HibuFilter[];
    removeSelection: () => void;
};

const HistogramCollapsed = React.memo(({isExpanded, selectedFilters, removeSelection}: HistogramCollapsedProps) => {
    if (isExpanded || !selectedFilters || selectedFilters.length <= 0) return null;
    const begin: number = new Date((selectedFilters as HibuRangeFilter[])[0].gte).valueOf();
    const end: number = new Date((selectedFilters as HibuRangeFilter[])[0].lt).valueOf();
    return (
        <div className="facet-value">
            <div
                className={`facet facet-selected`}>
                <Checkbox value={true} mayEdit={true}
                          labelKey={formatDateShort(begin) + ' - ' + formatDateShort(end)}
                          onChange={() => {
                              removeSelection();
                          }}
                          isRawHtml={false}
                >
                </Checkbox>
            </div>
        </div>
    );
});
